/**
 * PORTED — RO-3-11 / #44304.
 * Origin: `excellent/app/database/duplicates/_client.tsx`.
 *
 * Changes, and only these:
 *   `"use client"`                                     -> dropped (inert here)
 *   `import type { DuplicateCandidate, EntitySummary }`
 *     from `@/database/duplicates-data`                -> the contract's
 *                                                         `DuplicatePairDto`
 *   the local `DuplicatePair` interface                 -> the same
 *
 * Those were `import type` (erased, never bundled), but they pointed at a SERVER
 * module's shapes. The contract owns them now, so there is one declaration that a
 * zod parse actually enforces at the boundary instead of two that agree by
 * inspection.
 *
 * `actions.mergeInto` stays a PROP rather than becoming an inline `apiCall`. That
 * is not inertia: this component owns the optimistic removal of a resolved pair
 * from its own list, and keeping the write injected means the same interaction
 * logic is exercisable without a network, which is how its origin was written and
 * how `ConflictQueue` beside it still works. The route supplies the
 * `duplicates.merge` call.
 *
 * The origin's header explains at length why this is NOT a generalisation of
 * `ConflictQueue`; that reasoning is unchanged by the port and is reproduced below
 * verbatim, because the next reader will have the same question.
 */

/**
 * DuplicateQueue — the interactive merge-review surface (#187, PRD §3.4).
 *
 * Why this is NOT a generalisation of ConflictQueue
 * ─────────────────────────────────────────────────
 * `ConflictQueue` (packages/ui/src/blocks/sync/conflict-queue.tsx) is built
 * around per-FIELD value divergence within a single entity: each card shows
 * `local_value` vs `incoming_value` for one column, and the decision is
 * "keep this device's value | keep the peer's value". Adding
 * `hideMergeInput`/label props would not bridge the structural gap — here we
 * have TWO SEPARATE ENTITIES that may be the same person/org, and the
 * decision is "merge A into B | merge B into A | dismiss", not "keep one
 * field value". The data shapes diverge at the type level, so composing
 * new Cards + list-rows is the right call (D9 — extend, never re-implement
 * at the wrong level).
 */

import { useState, useTransition } from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  formatRelativeTime,
  IntentBadge,
} from "@excellent/ui";
import type {
  DuplicatePairDto,
  DuplicateEntitySummaryDto,
} from "@excellent/ui/api/contracts/database/duplicates";

export interface DuplicateQueueActions {
  /**
   * Merge `fromUrn` into `toUrn`.
   * Returns { ok: false, reason: "stub" } while the detector is stubbed.
   */
  mergeInto: (
    fromUrn: string,
    toUrn: string,
  ) => Promise<{ ok: boolean; reason?: string }>;
}

// ---------------------------------------------------------------------------
// Queue root
// ---------------------------------------------------------------------------

export function DuplicateQueue({
  pairs,
  actions,
  stubActive,
}: {
  pairs: DuplicatePairDto[];
  actions: DuplicateQueueActions;
  stubActive: boolean;
}) {
  const [items, setItems] = useState(pairs);

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
          <p className="type-body text-foreground">No duplicate candidates</p>
          <p className="max-w-sm type-subtext text-muted-foreground">
            The detector runs after every sync. When two records share the same
            email, phone, or org domain + name, the pair surfaces here for
            review. Auto-merge never happens — a human or a verified agent task
            confirms every merge.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((p) => (
        <DuplicatePairCard
          key={`${p.candidate.a_urn}::${p.candidate.b_urn}`}
          pair={p}
          stubActive={stubActive}
          actions={actions}
          onResolved={() =>
            setItems((prev) =>
              prev.filter(
                (x) =>
                  x.candidate.a_urn !== p.candidate.a_urn ||
                  x.candidate.b_urn !== p.candidate.b_urn,
              ),
            )
          }
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-pair card
// ---------------------------------------------------------------------------

function DuplicatePairCard({
  pair,
  stubActive,
  actions,
  onResolved,
}: {
  pair: DuplicatePairDto;
  stubActive: boolean;
  actions: DuplicateQueueActions;
  onResolved: () => void;
}) {
  const [pending, startTransition] = useTransition();
  const [toast, setToast] = useState<{
    kind: "error" | "stub";
    message: string;
  } | null>(null);

  function handleMerge(fromUrn: string, toUrn: string) {
    setToast(null);
    startTransition(async () => {
      const res = await actions.mergeInto(fromUrn, toUrn);
      if (res.ok) {
        onResolved();
      } else if (res.reason === "stub") {
        setToast({
          kind: "stub",
          message: "Merge lands when #185 ships — the merge engine isn't in this lane yet.",
        });
      } else {
        setToast({
          kind: "error",
          message: res.reason ?? "Could not complete the merge. Please retry.",
        });
      }
    });
  }

  function handleDismiss() {
    // v1: client-side hide only.  A durable dismiss table is tracked as a
    // follow-up — the candidate re-surfaces on the next detector pass if
    // not confirmed or dismissed persistently.
    onResolved();
  }

  const scoreIntent = scoreToIntent(pair.candidate.score);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="type-title">Possible duplicate</CardTitle>
          <div className="flex shrink-0 items-center gap-2">
            <IntentBadge intent={scoreIntent}>
              {pair.candidate.reason.replace(/-/g, " ")}
            </IntentBadge>
            <span className="tabular-nums type-subtext text-muted-foreground">
              {Math.round(pair.candidate.score * 100)}%
            </span>
          </div>
        </div>
        <CardDescription className="type-subtext text-muted-foreground">
          Review these two records and decide whether to merge them, or dismiss
          the suggestion.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-px">
        <EntityRow label="A" entity={pair.a} />
        <EntityRow label="B" entity={pair.b} />
      </CardContent>

      <CardFooter className="flex flex-col items-stretch gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {/* Primary: merge A into B (B wins) */}
          <Button
            size="sm"
            variant="accept"
            disabled={pending}
            onClick={() => handleMerge(pair.candidate.a_urn, pair.candidate.b_urn)}
          >
            Merge A into B
          </Button>
          {/* Secondary: merge B into A (A wins) */}
          <Button
            size="sm"
            variant="accept"
            disabled={pending}
            onClick={() => handleMerge(pair.candidate.b_urn, pair.candidate.a_urn)}
          >
            Merge B into A
          </Button>
          {/* Tertiary: dismiss */}
          <Button
            size="sm"
            variant="ghost"
            disabled={pending}
            onClick={handleDismiss}
            className="text-muted-foreground"
          >
            Dismiss
          </Button>
        </div>

        {toast && (
          <p
            className={
              toast.kind === "stub"
                ? "type-subtext text-muted-foreground"
                : "type-subtext text-destructive"
            }
          >
            {toast.message}
          </p>
        )}
      </CardFooter>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Entity summary row
// ---------------------------------------------------------------------------

function EntityRow({ label, entity }: { label: string; entity: DuplicateEntitySummaryDto }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/40 py-2 first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <span className="shrink-0 type-subtext font-medium text-muted-foreground">
            {label}
          </span>
          <span className="truncate type-body text-foreground">{entity.name}</span>
        </div>
        <div className="flex items-center gap-2 type-subtext text-muted-foreground">
          <span className="capitalize">{entity.kind}</span>
          {entity.createdAt && (
            <>
              <span aria-hidden>·</span>
              <span>Created {formatRelativeTime(entity.createdAt)}</span>
            </>
          )}
        </div>
      </div>
      <code className="shrink-0 truncate font-mono type-subtext text-muted-foreground max-w-[14ch]">
        {entity.urn}
      </code>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Score → intent
// ---------------------------------------------------------------------------

function scoreToIntent(score: number): "success" | "warning" | "danger" {
  if (score >= 0.95) return "danger"; // very high confidence: treat as alert
  if (score >= 0.8) return "warning";
  return "success";
}
